/**
 * Per-request context propagated via Node AsyncLocalStorage.
 *
 * Purpose: a route handler (or anything it calls) can emit a log
 * without knowing the caller's request ID. The logger reads the
 * current store and stamps `request_id`, `user_id`, `path`,
 * `method` onto every line for that request — giving us a
 * correlation handle across nested service calls.
 *
 * Wire-up:
 *   - `src/middleware.ts` generates a request ID and forwards it
 *     on the inbound request header `x-request-id` (and echoes it
 *     on the outbound response header for client-side breadcrumbs).
 *   - `src/lib/api/withSchema.ts` wraps the handler in
 *     `runWithRequestContext({ requestId, path, method }, handler)`,
 *     so every Zod-validated route gets correlation for free.
 *   - Non-withSchema routes can call `runWithRequestContext` at
 *     their entry point, or they just won't have correlation.
 *     Acceptable today; tightens as the withSchema ratchet moves.
 *
 * Browser-safety: this module is transitively imported by the
 * shared `monitoring/logger.ts`, which is pulled into some client
 * bundles. `node:async_hooks` would blow up the Turbopack client
 * build, so we detect the runtime and fall back to a no-op store
 * in the browser. The logger then simply sees "no request context"
 * and emits un-correlated lines — exactly the right behavior on
 * the client anyway.
 */

type Store = {
    getStore(): RequestContext | undefined
    run<T>(ctx: RequestContext, fn: () => T): T
}

export interface RequestContext {
    requestId: string
    userId?: string
    path?: string
    method?: string
    // Extra free-form metadata (e.g. feature flag, tenant id).
    meta?: Record<string, unknown>
}

function createStore(): Store {
    // Try to load Node's AsyncLocalStorage. In the browser bundle this
    // `require` is never reached at runtime (we short-circuit on
    // `window`), but wrapping in try/catch also handles the Edge
    // runtime + any future sandbox where the module is missing. In
    // the happy-dom Vitest environment `window` exists but
    // `async_hooks` also exists via Node underneath — so we prefer
    // the require path when it resolves.
    try {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const mod = require('node:async_hooks') as typeof import('node:async_hooks')
        if (mod && mod.AsyncLocalStorage) {
            return new mod.AsyncLocalStorage<RequestContext>()
        }
    } catch {
        // fall through to the no-op store
    }

    return {
        getStore: () => undefined,
        run: <T,>(_ctx: RequestContext, fn: () => T) => fn(),
    }
}

const storage = createStore()

export function getRequestContext(): RequestContext | undefined {
    return storage.getStore()
}

export function runWithRequestContext<T>(
    ctx: RequestContext,
    fn: () => T,
): T {
    return storage.run(ctx, fn)
}

/**
 * Mutate the current request context if one exists. Safe no-op
 * when called outside any request. Typical use: after auth
 * resolves, stamp the user id on the in-flight context so later
 * log lines include it.
 */
export function updateRequestContext(patch: Partial<RequestContext>): void {
    const current = storage.getStore()
    if (!current) return
    Object.assign(current, patch)
}

/**
 * Generate a request-scoped correlation ID. Format is a short
 * `req_<timestamp36>_<rand36>` — human-greppable in log output.
 * Not a UUID: we don't need collision resistance at that level,
 * and shorter IDs are kinder to the terminal.
 */
export function generateRequestId(): string {
    const ts = Date.now().toString(36)
    const rand = Math.random().toString(36).slice(2, 10)
    return `req_${ts}_${rand}`
}
